import { BossIcon } from '../bosses/BossIcon';
import { CollapsibleSection } from '../goals/CollapsibleSection';
import { PitchedItemRow } from './PitchedItemRow';
import { PitchedDropStepper } from './PitchedDropStepper';
import {
  bossNameFor,
  characterDropTotal,
  countableBossSlugs,
  groupPitchedItemsByBoss,
  orderBrilliantItems,
  pitchedTotals,
} from '../../lib/pitchedGrouping';
import type { PitchedDropCountDto } from '../../lib/pitched';
import type { BossDto, PitchedItemDto } from '../../lib/reference';

interface CharacterPitchedListProps {
  characterName: string;
  characterId: number;
  items: PitchedItemDto[];
  bossCatalog: BossDto[];
  obtainedSlugs: string[];
  pendingSlug: string | null;
  onToggle: (item: PitchedItemDto, obtained: boolean) => void;
  /** REV17 — this character's lifetime drop counts (independent of `obtainedSlugs`). */
  dropCounts: PitchedDropCountDto[];
  /** REV17 — `bossSlug` of the drop-count PUT currently in flight for this character, if any. */
  pendingDropBossSlug: string | null;
  onDropCountChange: (bossSlug: string, count: number) => void;
}

/**
 * One character's Pitched-tracker tab content (REV6 spec): Pitched-set items grouped by source
 * boss (boss catalog order, boss icon + name per group), then a trailing "Brilliant Boss Set"
 * group listing every Brilliant item flat (each carrying its own source boss as microtext since
 * they're not sub-grouped) — Bellona's Hard-mode Brilliant drop appears there like any other,
 * distinguished only by the unverified badge and a "KMS" chip (REV6 spec's pre-GMS caveat).
 */
export function CharacterPitchedList({
  characterName,
  characterId,
  items,
  bossCatalog,
  obtainedSlugs,
  pendingSlug,
  onToggle,
  dropCounts,
  pendingDropBossSlug,
  onDropCountChange,
}: CharacterPitchedListProps) {
  const obtained = new Set(obtainedSlugs);
  const groups = groupPitchedItemsByBoss(items, bossCatalog);
  const brilliantItems = orderBrilliantItems(items, bossCatalog);
  const totals = pitchedTotals(items, obtainedSlugs);
  const dropCountFor = (bossSlug: string) => dropCounts.find((d) => d.bossSlug === bossSlug)?.count ?? 0;

  // REV17 — countable bosses with no Pitched-item group of their own: every Brilliant item's
  // source boss (Bellona, Jupiter, Limbo, Baldrix, First Adversary, Malefic Star — the flat
  // "Brilliant Boss Set" list below stays un-grouped, per REV6's own design note) plus Kaling,
  // the one pure box-only dropper. Rendered as plain rows, not collapsible sections — there is no
  // item list to hide, so there's no toggle for a counter click to fight.
  const groupedBossSlugs = new Set(groups.map((g) => g.bossSlug));
  const otherBosses = bossCatalog.filter(
    (boss) => countableBossSlugs(items).has(boss.slug) && !groupedBossSlugs.has(boss.slug),
  );

  return (
    <div
      id={`pitched-tabpanel-${characterId}`}
      role="tabpanel"
      aria-labelledby={`pitched-tab-${characterId}`}
      className="space-y-4"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-sm font-semibold uppercase tracking-wider text-text-faint">
          {characterName}&rsquo;s Pitched items
        </h2>
        <p className="whitespace-nowrap text-xs font-medium text-text-faint">
          {totals.pitchedObtained}/{totals.pitchedTotal} Pitched · {totals.brilliantObtained}/{totals.brilliantTotal}{' '}
          Brilliant · {characterDropTotal(dropCounts)} lifetime drops
        </p>
      </div>

      <div className="space-y-4">
        {groups.map((group) => {
          const clearedCount = group.items.filter((i) => obtained.has(i.slug)).length;
          return (
            <CollapsibleSection
              key={group.bossSlug}
              title={
                <span className="flex items-center gap-2">
                  <BossIcon slug={group.bossSlug} name={group.bossName} />
                  {group.bossName}
                </span>
              }
              subtitle={`${clearedCount}/${group.items.length} obtained`}
              headerAction={
                <PitchedDropStepper
                  count={dropCountFor(group.bossSlug)}
                  pending={pendingDropBossSlug === group.bossSlug}
                  onChange={(next) => onDropCountChange(group.bossSlug, next)}
                  label={`${group.bossName} drops for ${characterName}`}
                />
              }
            >
              <ul className="space-y-2">
                {group.items.map((item) => (
                  <PitchedItemRow
                    key={item.slug}
                    item={item}
                    obtained={obtained.has(item.slug)}
                    pending={pendingSlug === item.slug}
                    onToggle={() => onToggle(item, !obtained.has(item.slug))}
                  />
                ))}
              </ul>
            </CollapsibleSection>
          );
        })}

        {brilliantItems.length > 0 && (
          <CollapsibleSection
            title="Brilliant Boss Set"
            subtitle={`${totals.brilliantObtained}/${totals.brilliantTotal} obtained`}
          >
            <ul className="space-y-2">
              {brilliantItems.map((item) => (
                <PitchedItemRow
                  key={item.slug}
                  item={item}
                  sourceBossName={bossNameFor(bossCatalog, item.sourceBossSlug)}
                  obtained={obtained.has(item.slug)}
                  pending={pendingSlug === item.slug}
                  onToggle={() => onToggle(item, !obtained.has(item.slug))}
                />
              ))}
            </ul>
          </CollapsibleSection>
        )}

        {otherBosses.length > 0 && (
          <section className="rounded-xl border border-border bg-bg-elevated">
            <div className="px-5 py-3">
              <p className="font-display text-base font-semibold text-text">Other drop sources</p>
              <p className="text-xs text-text-faint">
                Brilliant-only bosses and Chaos Black Accessory Box droppers — lifetime counters only.
              </p>
            </div>
            <ul className="space-y-2 border-t border-border p-5 pt-4">
              {otherBosses.map((boss) => (
                <li
                  key={boss.slug}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-bg-elevated-hover px-3 py-2"
                >
                  <span className="flex items-center gap-2 text-sm font-medium text-text">
                    <BossIcon slug={boss.slug} name={boss.name} />
                    {boss.name}
                  </span>
                  <PitchedDropStepper
                    count={dropCountFor(boss.slug)}
                    pending={pendingDropBossSlug === boss.slug}
                    onChange={(next) => onDropCountChange(boss.slug, next)}
                    label={`${boss.name} drops for ${characterName}`}
                  />
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </div>
  );
}
